// Session de magnétisme : l'unique point de contact entre le drag existant et
// le moteur. Un module singleton plutôt qu'un contexte React, pour trois
// raisons : le drag n'a jamais lieu deux fois en parallèle, les appelants sont
// à la fois des composants (aperçu de drag) et des callbacks hors React
// (drop react-dnd, drag souris des tableaux), et rien de tout cela ne doit
// provoquer de re-render.
//
// Cycle de vie : beginSnapSession (dragstart) → resolveSnapOffset (chaque
// mouvement, PURE) → flushSnap (publication + haptique, une fois par frame) →
// endSnapSession (dragend / mouseup). Hors session, resolveSnapOffset rend
// l'offset brut inchangé : le comportement d'origine est le comportement par
// défaut, y compris si un maillon manque.

import { marginsObject } from '../constants/gridConfig';
import { effectiveZoom } from '../utils/zoom';
import { useStore } from '../store/store';
import { collectSnapTargets } from './collectSnapTargets';
import { pulseOnSnapChange, resetHaptics } from './haptics';
import { clearSnap, publishSnap } from './snapBus';
import { SNAP_CONFIG } from './snapConfig';
import { computeResizeSnap, computeSnap, guidesSignature, prepareSnapTargets } from './snapGeometry';

let session = null;

// Cmd (macOS) / Ctrl : désactive temporairement le magnétisme, convention
// partagée par Figma, Sketch et Illustrator. Les événements de drag HTML5
// portent l'état des modificateurs — c'est la seule source fiable, un
// keydown/keyup n'est pas garanti pendant un drag natif.
const readModifier = (event) => {
  if (!session) return;
  session.disabledByModifier = !!(event.metaKey || event.ctrlKey);
};

const attachModifierListeners = () => {
  window.addEventListener('dragover', readModifier, { capture: true, passive: true });
  window.addEventListener('mousemove', readModifier, { capture: true, passive: true });
};

const detachModifierListeners = () => {
  window.removeEventListener('dragover', readModifier, { capture: true });
  window.removeEventListener('mousemove', readModifier, { capture: true });
};

/**
 * Ouvre une session. À n'appeler que pour une box en mode dessin (colonne
 * drawMode ou masque) : ailleurs les box sont positionnées par le flux, pas en
 * coordonnées libres, et le magnétisme n'a pas de sens.
 *
 * @param {string} parentId id de la colonne (ou du masque) porteuse — sert à
 *   router les guides vers le bon calque, pas à mesurer le DOM
 * @param {string} draggedId id de la box déplacée
 * @param {HTMLElement} [draggedEl] nœud déplacé, d'où l'on remonte le repère
 * @param {string[]} [excludeIds] ids exclus des cibles (défaut : la box seule)
 */
export const beginSnapSession = ({ parentId, draggedId, draggedEl, excludeIds }) => {
  endSnapSession();
  if (!SNAP_CONFIG.enabled || !parentId || !draggedId) return;

  const { zoomLevel, landscape, configuration } = useStore.getState();
  const zoomFactor = effectiveZoom(zoomLevel, landscape) / 100;
  if (!zoomFactor) return;

  const collected = collectSnapTargets({
    draggedEl,
    draggedId,
    excludeIds: excludeIds ?? [draggedId],
    zoomFactor,
    // Mêmes marges que celles réellement dessinées sur la page (repères
    // visibles par l'utilisateur), pas une reconstruction parallèle.
    margins: marginsObject(configuration),
  });
  if (!collected) return;

  session = {
    parentId,
    origin: collected.dragged,
    targets: prepareSnapTargets(collected.targets),
    // Seuils exprimés en px écran dans la config : ramenés en px document pour
    // que la sensation d'accrochage ne dépende pas du niveau de zoom.
    threshold: SNAP_CONFIG.snapThreshold / zoomFactor,
    overhang: SNAP_CONFIG.guideOverhang / zoomFactor,
    disabledByModifier: false,
    lastOffset: null,
    lastDisabled: false,
    lastResult: null,
    lastSnappedOffset: null,
    publishedSignature: '',
  };
  attachModifierListeners();
};

/**
 * Applique le magnétisme à un déplacement. Pure et mémoïsée sur son entrée :
 * l'appeler pendant le rendu de l'aperçu puis à nouveau au drop donne
 * exactement la même position — l'utilisateur obtient ce qu'il a vu.
 *
 * @param {{x:number,y:number}} offset déplacement brut, en px document
 * @returns {{x:number,y:number}} déplacement accroché (l'objet d'entrée si aucune session)
 */
export const resolveSnapOffset = (offset) => {
  if (!session || !offset || !Number.isFinite(offset.x) || !Number.isFinite(offset.y)) {
    return offset;
  }
  // L'état du modificateur fait partie de la clé de cache : relâcher Cmd sans
  // bouger la souris doit refaire apparaître les guides.
  if (
    session.lastOffset &&
    session.lastOffset.x === offset.x &&
    session.lastOffset.y === offset.y &&
    session.lastDisabled === session.disabledByModifier
  ) {
    return session.lastSnappedOffset;
  }
  session.lastOffset = offset;
  session.lastDisabled = session.disabledByModifier;
  if (session.disabledByModifier) {
    session.lastResult = null;
    session.lastSnappedOffset = offset;
    return offset;
  }
  const result = computeSnap({
    draggedBBox: {
      ...session.origin,
      x: session.origin.x + offset.x,
      y: session.origin.y + offset.y,
    },
    externalBBoxes: session.targets,
    threshold: session.threshold,
    guideOverhang: session.overhang,
  });
  const snappedOffset = { x: offset.x + result.deltaX, y: offset.y + result.deltaY };
  session.lastResult = result;
  session.lastSnappedOffset = snappedOffset;
  return snappedOffset;
};

/**
 * Applique le magnétisme à un redimensionnement (poignées droite/bas, coin
 * haut-gauche fixe). Même session que le déplacement : mêmes cibles, même
 * Cmd/Ctrl pour s'en affranchir, mêmes guides via flushSnap.
 *
 * @param {{width:number,height:number}} size taille brute, en px document
 * @param {{x:boolean,y:boolean}} axes axes que la poignée redimensionne
 * @returns {{width:number,height:number}} taille accrochée (l'objet d'entrée si aucune session)
 */
export const resolveSnapResize = (size, axes) => {
  if (!session || !size) return size;
  const usable = {
    x: !!axes?.x && Number.isFinite(size.width),
    y: !!axes?.y && Number.isFinite(size.height),
  };
  if (session.disabledByModifier || (!usable.x && !usable.y)) {
    session.lastResult = null;
    return size;
  }
  const result = computeResizeSnap({
    bbox: {
      ...session.origin,
      width: usable.x ? size.width : session.origin.width,
      height: usable.y ? size.height : session.origin.height,
    },
    axes: usable,
    externalBBoxes: session.targets,
    threshold: session.threshold,
    guideOverhang: session.overhang,
  });
  session.lastResult = result;
  return {
    width: usable.x ? result.width : size.width,
    height: usable.y ? result.height : size.height,
  };
};

// Effets de bord du dernier resolveSnapOffset : publication des guides et
// impulsion haptique. Séparé du calcul pour que ce dernier reste appelable
// pendant un rendu React sans effet de bord (et donc en StrictMode).
export const flushSnap = () => {
  if (!session) return;
  const guides = session.lastResult?.activeGuides ?? [];
  const signature = guidesSignature(guides);
  // Republié même à signature constante : quand la box glisse LE LONG d'un
  // guide, sa position ne bouge pas mais l'étendue du trait, si. Le calque
  // coalesce sous requestAnimationFrame, le coût reste d'un repaint par frame.
  if (guides.length > 0 || session.publishedSignature !== '') {
    publishSnap({ active: true, parentId: session.parentId, guides });
  }
  session.publishedSignature = signature;
  // Le verrou anti-rafale vit dans le helper haptique : l'appeler à chaque
  // frame est sans effet tant que l'alignement ne change pas.
  pulseOnSnapChange(signature);
};

export const endSnapSession = () => {
  if (!session) return;
  session = null;
  detachModifierListeners();
  resetHaptics();
  clearSnap();
};
